import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  CheckCircle, Clock, XCircle, Users, BookOpen, Award, 
  Download, RefreshCw, ChevronDown, ChevronUp, FileCheck,
  AlertTriangle
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

import { apiFetch } from '../services/api';
// ============================================================================
// STATUS BADGE KOMPONENTE
// ============================================================================
const StatusBadge = ({ status }) => {
  const config = {
    completed: { 
      icon: CheckCircle, 
      text: 'Abgeschlossen', 
      className: 'bg-green-500/20 text-green-400 border-green-500/30' 
    },
    in_progress: { 
      icon: Clock, 
      text: 'In Arbeit', 
      className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' 
    },
    not_started: { 
      icon: XCircle, 
      text: 'Offen', 
      className: 'bg-gray-500/20 text-gray-400 border-gray-500/30' 
    }
  };

  const { icon: Icon, text, className } = config[status] || config.not_started;

  return (
    <Badge className={`${className} border flex items-center gap-1 px-2 py-1`}>
      <Icon className="w-3 h-3" />
      <span className="text-xs">{text}</span>
    </Badge>
  );
};

// ============================================================================
// FORTSCHRITTSBALKEN
// ============================================================================
const ProgressBar = ({ value, className = '' }) => (
  <div className={`h-2 bg-gray-700 rounded-full overflow-hidden ${className}`}>
    <div 
      className="h-full bg-gradient-to-r from-yellow-500 to-green-500 transition-all duration-500"
      style={{ width: `${value}%` }}
    />
  </div>
);

// ============================================================================
// SCHULUNGSMATRIX KOMPONENTE
// ============================================================================
const SchulungsMatrix = ({ companyId }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [matrixData, setMatrixData] = useState(null);
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  const [viewMode, setViewMode] = useState('employees'); // 'employees' or 'courses'

  useEffect(() => {
    loadMatrix();
  }, []);

  const loadMatrix = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/matrix`, {
        headers: { }
      });
      const data = await response.json();
      if (data.success) {
        setMatrixData(data);
      }
    } catch (error) {
      console.error('Error loading matrix:', error);
      toast({
        title: "Fehler",
        description: "Schulungsmatrix konnte nicht geladen werden",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const exportMatrix = () => {
    if (!matrixData) return;

    // CSV Export
    let csv = 'Mitarbeiter,E-Mail,';
    csv += matrixData.courses.map(c => c.title).join(',') + ',Fortschritt\n';

    matrixData.matrix.forEach(emp => {
      csv += `"${emp.employeeName}","${emp.employeeEmail}",`;
      csv += emp.courses.map(c => {
        if (c.status === 'completed') return 'Abgeschlossen';
        if (c.status === 'in_progress') return 'In Arbeit';
        return 'Offen';
      }).join(',');
      csv += `,${emp.completionRate}%\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `schulungsmatrix_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();

    toast({
      title: "Export erfolgreich",
      description: "Schulungsmatrix wurde als CSV heruntergeladen"
    });
  };

  if (loading) {
    return (
      <Card className="bg-gray-900/50 border-gray-800">
        <CardContent className="py-12 text-center">
          <RefreshCw className="w-8 h-8 text-yellow-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Lade Schulungsmatrix...</p>
        </CardContent>
      </Card>
    );
  }

  if (!matrixData || matrixData.matrix.length === 0) {
    return (
      <Card className="bg-gray-900/50 border-gray-800">
        <CardContent className="py-12 text-center">
          <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 mb-2">Noch keine Mitarbeiter vorhanden</p>
          <p className="text-gray-500 text-sm">Fügen Sie Mitarbeiter hinzu, um die Schulungsmatrix zu sehen</p>
        </CardContent>
      </Card>
    );
  }

  const stats = matrixData.statistics;

  return (
    <div className="space-y-6">
      {/* Header mit Statistiken */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase">Mitarbeiter</p>
                <p className="text-2xl font-bold text-white">{stats.totalEmployees}</p>
              </div>
              <Users className="w-8 h-8 text-yellow-500/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase">Kurse</p>
                <p className="text-2xl font-bold text-white">{stats.totalCourses}</p>
              </div>
              <BookOpen className="w-8 h-8 text-blue-500/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase">Abgeschlossen</p>
                <p className="text-2xl font-bold text-green-500">{stats.totalCompletions}</p>
              </div>
              <Award className="w-8 h-8 text-green-500/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase">Fortschritt</p>
                <p className="text-2xl font-bold text-yellow-500">{stats.overallCompletionRate}%</p>
              </div>
              <FileCheck className="w-8 h-8 text-yellow-500/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Matrix Card */}
      <Card className="bg-gray-900/50 border-gray-800">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-yellow-500" />
              Schulungsmatrix
            </CardTitle>
            <CardDescription>Übersicht aller Mitarbeiter und deren Schulungsstatus</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={loadMatrix}
              className="border-gray-700 text-gray-300"
            >
              <RefreshCw className="w-4 h-4 mr-1" /> Aktualisieren
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={exportMatrix}
              className="border-yellow-500/50 text-yellow-500"
            >
              <Download className="w-4 h-4 mr-1" /> CSV Export
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {/* Legende */}
          <div className="flex flex-wrap gap-4 mb-6 pb-4 border-b border-gray-800">
            <div className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span className="text-gray-400">Abgeschlossen</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <span className="text-gray-400">In Arbeit</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded-full bg-gray-500" />
              <span className="text-gray-400">Noch offen</span>
            </div>
          </div>

          {/* Matrix Tabelle */}
          <div className="space-y-3">
            {matrixData.matrix.map((employee) => (
              <div 
                key={employee.employeeId} 
                className="bg-black/30 rounded-lg border border-gray-800 overflow-hidden"
              >
                {/* Employee Header */}
                <div 
                  className="p-4 cursor-pointer hover:bg-gray-800/50 transition-colors"
                  onClick={() => setExpandedEmployee(
                    expandedEmployee === employee.employeeId ? null : employee.employeeId
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-full bg-yellow-500/20 flex items-center justify-center text-yellow-500 font-bold">
                        {employee.employeeName.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-white font-medium">{employee.employeeName}</p>
                        <p className="text-sm text-gray-500">{employee.employeeEmail}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      {/* Kurs-Status-Dots */}
                      <div className="hidden md:flex gap-1">
                        {employee.courses.slice(0, 7).map((course, idx) => (
                          <div 
                            key={idx}
                            className={`w-3 h-3 rounded-full ${
                              course.status === 'completed' ? 'bg-green-500' :
                              course.status === 'in_progress' ? 'bg-yellow-500' : 'bg-gray-600'
                            }`}
                            title={`${course.courseTitle}: ${course.status}`}
                          />
                        ))}
                      </div>

                      {/* Fortschritt */}
                      <div className="text-right min-w-[100px]">
                        <p className="text-lg font-bold text-white">{employee.completionRate}%</p>
                        <p className="text-xs text-gray-500">{employee.completedCount}/{employee.totalCourses} Kurse</p>
                      </div>

                      {/* Expand Icon */}
                      {expandedEmployee === employee.employeeId ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-3">
                    <ProgressBar value={employee.completionRate} />
                  </div>
                </div>

                {/* Expanded Course Details */}
                {expandedEmployee === employee.employeeId && (
                  <div className="border-t border-gray-800 p-4 bg-gray-900/30">
                    <div className="grid gap-2">
                      {employee.courses.map((course, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center justify-between py-2 px-3 bg-black/30 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${
                              course.status === 'completed' ? 'bg-green-500' :
                              course.status === 'in_progress' ? 'bg-yellow-500' : 'bg-gray-500'
                            }`} />
                            <span className="text-gray-300 text-sm">{course.courseTitle}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            {course.status === 'in_progress' && (
                              <span className="text-xs text-gray-500">{course.progress}%</span>
                            )}
                            <StatusBadge status={course.status} />
                            {course.certificateId && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-yellow-500 h-7 px-2"
                              >
                                <Download className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Warnungen */}
          {stats.overallCompletionRate < 50 && (
            <div className="mt-6 p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-orange-400 font-medium">Schulungsrückstand erkannt</p>
                <p className="text-gray-400 text-sm mt-1">
                  Weniger als 50% der Pflichtschulungen sind abgeschlossen. 
                  Erinnern Sie Ihre Mitarbeiter an ausstehende Kurse.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SchulungsMatrix;
